import { State, Selector, Action } from '@ngxs/store';
import { CapaciteApiService } from './services/capacite-api/capacite-api.service';
import { Router } from '@angular/router';
import { NgZone, Injectable } from '@angular/core';
import { cloneDeep, get } from 'lodash';
import idx from 'idx';
import { ReloadPropriete } from '../../propriete/propriete.state';
import { CapaciteModel, CapacitePartial } from './modeles/capacite.model';
import { CapaciteHelper } from './helpers/capacite-helper/capacite-helper';
import { CapaciteEditStepEnum } from './constants/capacite-edit-step.enum';
import { CapaciteStepEnum } from './constants/capacite-step.enum';
import { HideLoader, ShowLoader, ShowPopup } from '../../shared/personne.state';
import { EtapeHypothequeDataModel } from './modeles/etape-hypotheque-data.model';
import { FrequenceEnum, FREQUENCES } from '../../shared/constants/frequence.config';
import { EtapeDepensesDataModel } from './modeles/etape-depenses-data.model';
import { CapaciteCybermetrieService } from './services/capacite-cybermetrie/capacite-cybermetrie.service';
import { TypeCapaciteEnum } from './constants/type-capacite.enum';
import { ResultatDataModel } from './modeles/resultat-data.model';
import { EtapePaiementDataModel } from './modeles/etape-paiement-data.model';
import { EtapeDettesDataModel } from './modeles/etape-dettes-data.model';
import { EtapeDepensesPaiementDataModel } from './modeles/etape-depenses-paiement-data.model';
import { FlowHelperService } from './services/flow-helper/flow-helper.service';
import { AppConfigService } from '../../app-config.service';
import { StatusCapaciteEnum } from './constants/status-capacite.enum';
import { TypeProprieteEnum } from './constants/type-propriete.enum';
import { TypeProjet } from '../../shared/constants/type-projet.enum';
import { DepenseResidenceModel } from './modeles/depense-residence.model';
import {
  CalculerCapacite,
  CalculerCapaciteFailed,
  CalculerCapaciteSuccess,
  CalculerScenarioCapacite,
  ChargerCapacite,
  ClearScenarioCapacite,
  GoNextStep,
  GoPreviousStep,
  GoToStep,
  ModifierCapacite,
  NavigatePageAccueil,
  NavigatePageFormulaire,
  NavigatePageModification,
  NavigatePageNonEligible,
  NavigatePageResultat,
  PatchCapaciteDebugOnly,
  PersistPendingChanges,
  SaveStepBudget,
  SaveStepDepenses,
  SaveStepDepensesTotales,
  SaveStepDettes,
  SaveStepHypotheque,
  SaveStepPaiement,
  SaveStepQuestionTypePropriete
} from './capacite.actions';

const capaciteDefaut: CapaciteModel = {
  bilanSommaire: {
    revenuAnnuelBrut: null,
    revenuNet: null,
    codeFrequenceRevenuNet: null,
    carteCredit: null,
    codeFrequenceCarteCredit: null,
    margeCredit: null,
    codeFrequenceMargeCredit: null,
    pretAuto: null,
    codeFrequencePretAuto: null,
    pretPersonnel: null,
    codeFrequencePretPersonnel: null,
    autrePret: null,
    codeFrequenceAutrePret: null
  },
  indicateurCarteCredit: null,
  miseDeFonds: null,
  id: null,
  depensesMensuels: [],
  indicateurMontantTotalDepenses: null,
  montantTotalDepenses: null,
  valeurProprieteMaximale: null,
  versementMensuelMaximal: null,
  primeAssureurHypothecaireMaximale: null,
  versementMensuelRealiste: null,
  valeurProprieteRealiste: null,
  primeAssureurHypothecaireRealiste: null,
  indicateurLimiteMontantVersement: null,
  montantLimiteVersementMensuel: null,
  idProduitTerme: null,
  idAmortissement: null,
  taux: null,
  montantPretMaximal: null,
  montantPretRealiste: null,
  typeCapacite: null,
  typePropriete: null,
  depensesEstimeesCapaciteMaximale: null,
  depensesEstimeesCapaciteRealiste: null,
  depensesResidenceCourante: null
};

export interface CapaciteStateModel {
  loaded: boolean;
  currentStep: CapaciteStepEnum;
  completed: boolean;
  capacite: CapaciteModel;
  capaciteScenario?: CapaciteModel;
  pendingChanges: CapacitePartial;
  modifyingStep?: CapaciteEditStepEnum;
  returnRoute?: string;
  fraisProprieteMoyenne: number;
}
@Injectable()
@State<CapaciteStateModel>({
  name: 'capacite',
  defaults: {
    loaded: false,
    currentStep: null,
    completed: false,
    capacite: capaciteDefaut,
    capaciteScenario: capaciteDefaut,
    pendingChanges: {},
    modifyingStep: null,
    returnRoute: null,
    fraisProprieteMoyenne: 0
  }
})
export class CapaciteState {
  constructor(
    private readonly capaciteApi: CapaciteApiService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly _capaciteCybermetrieService: CapaciteCybermetrieService,
    private readonly flowHelper: FlowHelperService,
    private readonly appConfig: AppConfigService
  ) {}

  static isResultNull(capacite: CapaciteModel) {
    return (
      capacite.montantPretMaximal === null &&
      capacite.montantPretRealiste === null &&
      capacite.primeAssureurHypothecaireMaximale === null &&
      capacite.primeAssureurHypothecaireRealiste === null &&
      capacite.valeurProprieteMaximale === null &&
      capacite.valeurProprieteRealiste === null &&
      capacite.versementMensuelMaximal === null &&
      capacite.versementMensuelRealiste === null
    );
  }

  static isCompleted(capacite: CapaciteModel) {
    return (
      capacite.montantPretMaximal !== null &&
      typeof capacite.montantPretMaximal !== 'undefined' &&
      capacite.montantPretRealiste !== null &&
      typeof capacite.montantPretRealiste !== 'undefined'
    );
  }

  static resetPendingChanges(morceau: CapacitePartial, pendingChanges: CapacitePartial) {
    const bilan = idx(pendingChanges, _ => _.bilanSommaire) || {};
    if (morceau.bilanSommaire) {
      for (const key of Object.keys(morceau.bilanSommaire)) {
        bilan[key] = null;
      }
    }

    for (const key of Object.keys(morceau)) {
      pendingChanges[key] = null;
    }
    pendingChanges.bilanSommaire = bilan;

    return pendingChanges;
  }

  @Action(ChargerCapacite)
  async getCapacite({ getState, patchState }) {
    const state = getState();

    if (!state.loaded) {
      const capacites = await this.capaciteApi.getCapacite().toPromise();
      const capacite = capacites[0];
      capacite.typeCapacite =
        this.appConfig.typeProjet === TypeProjet.PremierAcheteur
          ? null
          : ((this.appConfig.typeProjet as string) as TypeCapaciteEnum); // TODO retirer TypeCapaciteEnum et le remplacer par TypeProjet après que les changements sur le backend sont faits
      const isCapaciteCompleted = CapaciteState.isCompleted(capacite);

      patchState({
        loaded: true,
        capacite,
        completed: isCapaciteCompleted
      });
    }
  }

  @Action(ModifierCapacite)
  async modifierCapacite({ getState, patchState, dispatch }, { morceauCapacite }: ModifierCapacite) {
    const state = getState();

    const nouvelleCapacite = CapaciteHelper.mergeCapacite(state.capacite, morceauCapacite);
    const capaciteAJour = await this.capaciteApi.updateCapacite(nouvelleCapacite).toPromise();
    const pendingChangesReset = CapaciteState.resetPendingChanges(morceauCapacite, state.pendingChanges);

    patchState({
      capacite: capaciteAJour,
      pendingChanges: pendingChangesReset
    });
    dispatch(new HideLoader());
  }

  @Action(GoNextStep)
  goNextStep({ dispatch }) {
    const nextStep = this.flowHelper.nextStep;
    if (nextStep) {
      dispatch(new GoToStep(nextStep));
    } else {
      dispatch(new NavigatePageAccueil());
    }
  }

  @Action(GoPreviousStep)
  goPreviousStep({ dispatch }) {
    const previousStep = this.flowHelper.previousStep;
    if (previousStep) {
      dispatch(new GoToStep(previousStep));
    } else {
      dispatch(new NavigatePageAccueil());
    }
  }

  @Action(GoToStep)
  goToStep({ patchState }, { step }: GoToStep) {
    patchState({ currentStep: step });
    window.scroll(0, 0);
  }

  @Action(NavigatePageFormulaire)
  async navigatePageFormulaire({ getState, dispatch }) {
    const state = getState();
    dispatch(new GoToStep(state.currentStep || this.flowHelper.steps[0]));
    await this.ngZone.run(async () => {
      await this.router.navigateByUrl('capacite/formulaire', { skipLocationChange: true });
    });
  }

  @Action(NavigatePageNonEligible)
  async navigatePageNonEligible() {
    await this.ngZone.run(async () => {
      await this.router.navigateByUrl('capacite/non-eligible', { skipLocationChange: true });
    });
  }

  @Action(NavigatePageAccueil)
  async navigatePageAccueil({ patchState }) {
    patchState({
      currentStep: null
    });
    await this.ngZone.run(async () => {
      await this.router.navigateByUrl('capacite');
    });
  }

  @Action(NavigatePageResultat)
  async navigatePageResulat({ patchState }, { isConfirmationVisble }: NavigatePageResultat) {
    patchState({
      currentStep: null,
      modifyingStep: null
    });
    await this.ngZone.run(async () => {
      await this.router.navigate(['capacite/resultat'], { queryParams: { confirmation: isConfirmationVisble } });
    });
  }

  @Action(NavigatePageModification)
  async navigatePageModification({ patchState }, { step }: NavigatePageModification) {
    patchState({
      modifyingStep: step
    });
    await this.ngZone.run(async () => {
      await this.router.navigateByUrl('capacite/modifier');
    });
  }

  @Action([SaveStepDepensesTotales, SaveStepDepenses, SaveStepPaiement])
  async saveLastStep({ dispatch }, { morceauCapacite }: SaveStepDepensesTotales) {
    await dispatch(new CalculerCapacite(morceauCapacite)).toPromise();
  }

  @Action([SaveStepBudget, SaveStepHypotheque, SaveStepDettes])
  async saveStepHypotheque({ dispatch }, { morceauCapacite }) {
    await dispatch(new ModifierCapacite(morceauCapacite)).toPromise();
    dispatch(new GoNextStep());
  }

  @Action(SaveStepQuestionTypePropriete)
  async saveStepQuestionTypePropriete({ dispatch }, { typePropriete }: SaveStepQuestionTypePropriete) {
    await dispatch(new ModifierCapacite({ typePropriete }));
  }

  @Action(PersistPendingChanges)
  persistPendingChanges({ patchState, dispatch }, { morceauCapacite }: PersistPendingChanges) {
    patchState({
      pendingChanges: morceauCapacite
    });
    dispatch(new HideLoader());
  }

  @Action(CalculerCapacite)
  async calculerCapacite({ getState, dispatch }, { morceauCapacite }: CalculerCapacite) {
    dispatch(new ShowLoader('calcul'));
    await dispatch(new ModifierCapacite(morceauCapacite)).toPromise();

    try {
      const capacite = await this.capaciteApi.calculResultat(getState().capacite).toPromise();

      if (CapaciteState.isResultNull(capacite)) {
        dispatch(new CalculerCapaciteFailed());
      } else {
        dispatch(new CalculerCapaciteSuccess(capacite));
      }
    } catch {
      dispatch(new CalculerCapaciteFailed());
    } finally {
      dispatch(new HideLoader());
    }
  }

  @Action(CalculerCapaciteSuccess)
  async calculerCapaciteSuccess({ patchState, dispatch, getState }, { capacite }: CalculerCapaciteSuccess) {
    patchState({
      loaded: true,
      capacite,
      completed: true
    });

    const isModifying = !!getState().modifyingStep;
    if (!isModifying) {
      this._capaciteCybermetrieService.pushFormulaireEvent('complete');
    }
    dispatch(new NavigatePageResultat(isModifying));
    await dispatch(new ReloadPropriete()).toPromise();
  }

  @Action(CalculerCapaciteFailed)
  async calculerCapaciteFailed({ getState, dispatch }) {
    const isModifying = !!getState().modifyingStep;
    dispatch(new HideLoader());
    dispatch(new ShowPopup('capacite-error', isModifying ? 'edit' : 'create'));
    this._capaciteCybermetrieService.pushFormulaireEvent('erreur technique', {
      messageErreur: 'erreur systeme calcul'
    });
  }

  @Action(CalculerScenarioCapacite)
  async calculerScenarioCapacite({ patchState, getState, dispatch }, { morceauCapacite }: CalculerScenarioCapacite) {
    dispatch(new ShowLoader('calcul'));
    const state = getState();

    const scenarioCapacite = CapaciteHelper.mergeCapacite(cloneDeep(state.capacite), morceauCapacite);
    const scenarioResultat = await this.capaciteApi.simulerResultat(scenarioCapacite).toPromise();

    if (!CapaciteState.isResultNull(scenarioResultat)) {
      patchState({
        capaciteScenario: CapaciteHelper.mergeCapacite(cloneDeep(state.capaciteScenario), scenarioResultat)
      });
    }

    dispatch(new HideLoader());
  }

  @Action(ClearScenarioCapacite)
  clearScenarioCapacite({ patchState }) {
    patchState({
      capaciteScenario: capaciteDefaut
    });
  }

  @Action(PatchCapaciteDebugOnly)
  patchCapaciteDebugOnly({ patchState, getState }, { capacite, isCompleted }: PatchCapaciteDebugOnly) {
    const state = getState();
    patchState({
      capacite: CapaciteHelper.mergeCapacite(state.capacite, capacite),
      completed: isCompleted,
      modifyingStep: null
    });
  }
}
